/**
 * Credit control.
 *
 * The aged debt analysis, and the notes that turn it into a conversation. A
 * promise to pay recorded against a customer is the difference between chasing
 * a debt and chasing it twice.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useTerms, lower } from '../lib/tenant';
import { date, humanise, money, percent, statusLabel, statusTone } from '../lib/format';
import { AsyncPanel, Badge, Card, DataTable, Money, PageHeader, Stat, type Column } from '../components/ui';

/**
 * One line of the aged analysis, exactly as the server sends it.
 *
 * `/receivables/aged-debtors` assembles its own object rather than passing the
 * SQL column names through, so the customer's identifiers arrive as `code`,
 * `name` and `type` with no `customer` prefix on them.
 */
interface AgedCustomer {
  customerId: string;
  code: string;
  name: string;
  type: string;
  buckets: Record<string, number>;
  totalMinor: number;
}

interface AgedDebtors {
  asAt: string;
  customers: AgedCustomer[];
  totals: Array<{ label: string; amountMinor: number }>;
  grandTotalMinor: number;
}

/**
 * A sales invoice as `/receivables/invoices` sends it — the row of the table
 * plus the joined customer. The money columns are BIGINT, which reaches the
 * client as a string from a PostgreSQL cluster and as a number under the
 * in-process driver, so each amount is coerced once at the point of display.
 */
interface SalesInvoiceRow {
  id: string;
  invoice_no: string;
  invoice_date: string;
  due_date: string;
  status: string;
  gross_total_minor: string;
  outstanding_minor: string;
  comments: string | null;
}

/**
 * The ageing buckets arrive as the SQL CASE values that produced them —
 * 'current', '1-30', '90+'. The people reading a debtors report never see the
 * database, so the columns are headed the way a credit controller says them.
 */
const BUCKET_LABELS: Record<string, string> = {
  current: 'Not yet due',
  '1-30': '1–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+': 'Over 90 days',
};

export function DebtorsPage() {
  const t = useTerms();
  const aged = useAsync(() => api.get<AgedDebtors>('/receivables/aged-debtors'), []);

  // The aged report answers "who owes what". The question straight after it is
  // always "what is that balance made up of", and there is no separate customer
  // screen to send the reader to, so the account opens underneath the analysis.
  const [selected, setSelected] = useState<AgedCustomer | null>(null);

  return (
    <>
      <PageHeader
        title="Credit control"
        subtitle={`Who owes what, and for how long. Choose a ${lower(t.customer)} to see the invoices behind the balance.`}
      />

      <AsyncPanel state={aged}>
        {(data) => {
          const labels = data.totals.map((bucket) => bucket.label);
          const overdue = data.totals
            .filter((bucket) => !/current|not yet/i.test(bucket.label))
            .reduce((sum, bucket) => sum + bucket.amountMinor, 0);

          const columns: Array<Column<AgedCustomer>> = [
            {
              key: 'customer',
              header: t.customer,
              sortValue: (c) => c.name,
              render: (c) => (
                <>
                  <div className="truncate">{c.name}</div>
                  <div className="smaller muted">
                    <span className="mono">{c.code}</span> · {humanise(c.type)}
                  </div>
                </>
              ),
            },
            ...labels.map((label) => ({
              key: label,
              header: BUCKET_LABELS[label] ?? label,
              numeric: true,
              sortValue: (c: AgedCustomer) => c.buckets[label] ?? 0,
              render: (c: AgedCustomer) => <Money minor={c.buckets[label] ?? 0} blankIfZero />,
            })),
            {
              key: 'total',
              header: 'Total',
              numeric: true,
              sortValue: (c) => c.totalMinor,
              render: (c) => <Money minor={c.totalMinor} bold />,
            },
          ];

          return (
            <div className="stack">
              <div className="grid grid--3">
                <Stat
                  label="Total owed"
                  value={money(data.grandTotalMinor)}
                  detail={`As at ${date(data.asAt)}`}
                  to="/sales"
                  drillLabel="Open the sales ledger"
                />
                <Stat
                  label="Overdue"
                  value={money(overdue)}
                  tone={overdue > 0 ? 'negative' : undefined}
                  detail={
                    overdue > 0
                      ? `${percent(data.grandTotalMinor > 0 ? (overdue / data.grandTotalMinor) * 100 : 0)} of the total`
                      : 'Nothing has passed its due date'
                  }
                />
                <Stat label={t.customerPlural} value={String(data.customers.length)} detail="with a balance outstanding" />
              </div>

              <Card
                title="Aged debt"
                subtitle={`Aged from the due date, as at ${date(data.asAt)}`}
                flush
                footer={
                  /*
                   * A ledger with nothing overdue leaves four columns blank, which
                   * reads as an unfinished table unless the screen says why it is
                   * empty. It is a result, not a gap.
                   */
                  overdue === 0 && data.customers.length > 0 ? (
                    <p className="smaller muted" style={{ margin: 0 }}>
                      Nothing has yet fallen due, so the ageing columns are empty. Each invoice is aged from its due
                      date rather than the date it was raised.
                    </p>
                  ) : undefined
                }
              >
                <DataTable
                  caption="Aged debtors by customer"
                  rows={data.customers}
                  rowKey={(c) => c.customerId}
                  emptyMessage="Nothing is outstanding."
                  // Credit control reads largest debt first, whatever order the
                  // server happened to return.
                  initialSort={{ key: 'total', direction: 'desc' }}
                  onRowClick={(c) => setSelected((current) => (current?.customerId === c.customerId ? null : c))}
                  columns={columns}
                  footer={
                    <tr className="is-total">
                      <td>Total</td>
                      {data.totals.map((bucket) => (
                        <td key={bucket.label} className="num">
                          {money(bucket.amountMinor, { blankIfZero: true })}
                        </td>
                      ))}
                      <td className="num">{money(data.grandTotalMinor)}</td>
                    </tr>
                  }
                />
              </Card>

              {selected ? (
                // Keyed on the customer so each account arrives as a fresh mount:
                // the loading state then belongs to the account being opened
                // rather than to the one that was on screen a moment ago.
                <CustomerAccount key={selected.customerId} customer={selected} onClose={() => setSelected(null)} />
              ) : null}
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}

/** The invoices behind one customer's balance. */
function CustomerAccount({ customer, onClose }: { customer: AgedCustomer; onClose: () => void }) {
  const invoices = useAsync(
    () => api.get<{ invoices: SalesInvoiceRow[] }>(`/receivables/invoices?customerId=${customer.customerId}`),
    [customer.customerId],
  );

  // On a short screen the account opens below the fold, and a row click that
  // changes nothing the reader can see looks like a control that does nothing.
  const panel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    panel.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  const columns: Array<Column<SalesInvoiceRow>> = [
    {
      key: 'invoice',
      header: 'Invoice',
      sortValue: (i) => i.invoice_no,
      render: (i) => (
        <>
          <div className="mono smaller">{i.invoice_no}</div>
          {i.comments ? <div className="smaller muted truncate">{i.comments}</div> : null}
        </>
      ),
    },
    { key: 'raised', header: 'Raised', sortValue: (i) => i.invoice_date, render: (i) => date(i.invoice_date) },
    { key: 'due', header: 'Due', sortValue: (i) => i.due_date, render: (i) => date(i.due_date) },
    {
      key: 'status',
      header: 'Status',
      sortValue: (i) => i.status,
      render: (i) => <Badge tone={statusTone(i.status)}>{statusLabel(i.status)}</Badge>,
    },
    {
      key: 'gross',
      header: 'Invoiced',
      numeric: true,
      sortValue: (i) => Number(i.gross_total_minor),
      render: (i) => <Money minor={Number(i.gross_total_minor)} />,
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      numeric: true,
      sortValue: (i) => Number(i.outstanding_minor),
      render: (i) => <Money minor={Number(i.outstanding_minor)} blankIfZero bold />,
    },
  ];

  return (
    <div ref={panel}>
      <Card
        title={customer.name}
        subtitle={`Account ${customer.code} · ${humanise(customer.type)}`}
        actions={
          <button type="button" className="btn btn--sm" onClick={onClose}>
            Close
          </button>
        }
        flush
      >
        <AsyncPanel state={invoices}>
          {(data) => {
            // Summed from the integers the server sent, only to total the column
            // the reader is already looking at.
            const outstanding = data.invoices.reduce((sum, invoice) => sum + Number(invoice.outstanding_minor), 0);

            return (
              <DataTable
                caption={`Sales invoices for ${customer.name}`}
                rows={data.invoices}
                rowKey={(i) => i.id}
                emptyMessage="No invoices have been raised on this account."
                initialSort={{ key: 'raised', direction: 'desc' }}
                columns={columns}
                footer={
                  <tr className="is-total">
                    <td colSpan={5}>Outstanding on this account</td>
                    <td className="num">{money(outstanding)}</td>
                  </tr>
                }
              />
            );
          }}
        </AsyncPanel>
      </Card>
    </div>
  );
}
